/**
 * SESAAT — Flow Ultra Service
 * ============================================================
 * Membaca data Flow Ultra SEBENAR untuk tab admin (Kelola Sistem → Flow).
 *
 * Koleksi:
 *   flow_subs/{emel}        — satu langganan pelanggan. WUJUD dan berisi.
 *                             Doc id = emel dinormalisasi (bukan uid).
 *   flow_accounts/{id}      — satu akaun family sharing + maklumat akaun induk.
 *                             ⚠️ BELUM WUJUD. Diisi daripada Google Sheet
 *                             pemilik. Lihat nota pada `bacaAkaun`.
 *   orders (type=flowultra) — rekod duit setiap bayaran Flow Ultra.
 *
 * ⛔ PEMBAHAGIAN YANG MESTI DIKEKALKAN:
 *   flow_subs = JAM (bila tamat, siapa duduk slot mana)
 *   orders    = DUIT (satu baris satu kali bayar)
 * Jangan campur. Bila "Buku Langganan" berpusat dibina nanti (Blok B dalam
 * PLAN_TINDAKAN_LANGGANAN.md), medan di sini sengaja sudah berbentuk sama
 * supaya pemindahannya jadi salinan, bukan reka bentuk semula.
 *
 * ⚠️ Nama medan pulangan sengaja mengikut nama yang FlowUltraTab sudah guna
 * (name/email/phone/days/stage/cap/t), supaya menyambung data sebenar tidak
 * memerlukan skrin itu ditulis semula.
 *
 * Rujukan: PLAN_TINDAKAN_LANGGANAN.md · PLAN_FLOW_ULTRA.md
 * ============================================================
 */

import { db, auth } from '../firebase';
import {
    collection, getDocs, query, where, addDoc, updateDoc, deleteDoc, doc, serverTimestamp,
} from 'firebase/firestore';

const SUBS = 'flow_subs';
const ACCOUNTS = 'flow_accounts';
const TENANTS = 'flow_tenants';
const ORDERS = 'orders';

export const DAY_MS = 86400000;

/** Timestamp Firestore → Date. Terima Date, Timestamp, atau null. */
function toDate(v) {
    if (!v) return null;
    if (v instanceof Date) return v;
    if (typeof v.toDate === 'function') return v.toDate();
    return null;
}

/**
 * Berapa hari lagi sebelum tarikh itu. Tamat hari ini = 0, semalam = -1.
 * ⚠️ Dikira dari TENGAH MALAM ke tengah malam, bukan jam ke jam — kalau tidak,
 * langganan yang tamat malam ini melaporkan "0 hari" pada pukul 9 pagi tetapi
 * "-1 hari" pada pukul 9 malam, dan angka pada skrin berubah sendiri sepanjang
 * hari tanpa sesiapa menyentuhnya.
 */
export function bakiHari(tarikh, kini) {
    if (!tarikh) return null;
    const a = new Date(kini.getFullYear(), kini.getMonth(), kini.getDate());
    const b = new Date(tarikh.getFullYear(), tarikh.getMonth(), tarikh.getDate());
    return Math.round((b - a) / DAY_MS);
}

/**
 * Satu langganan → bentuk yang skrin guna.
 *
 * TIGA TAHAP, setiap satu kerja berbeza:
 *   unassigned — sudah bayar, belum ada slot      → cari slot untuk dia
 *   ready      — ada slot, butiran belum dihantar → WhatsApp dia
 *   active     — hidup                            → pantau tarikh
 */
function petakanSub(id, d, kini) {
    const expiresAt = toDate(d.expiresAt);
    const startedAt = toDate(d.startedAt);
    const days = bakiHari(expiresAt, kini);

    /* Borang bayar tidak pernah meminta nama sebenar, jadi `contactName` kerap
       hanya emel yang sama. Memaparkannya sebagai "nama" membuat skrin nampak
       seperti ia tahu sesuatu yang ia sebenarnya tidak tahu. */
    /* Fasa 3A (3 Okt 2026): rekod yang admin masuk MANUAL kini juga di sini
       (source:'manual') — email boleh kosong, jadi id TIDAK dipakai sebagai email. */
    const manual = d.source === 'manual';
    const email = manual ? (d.email || '') : (d.email || id);
    const name = d.contactName && d.contactName !== email ? d.contactName : '';

    const adaSlot = !!d.accountId;
    const sent = !!toDate(d.credentialsSentAt);

    let stage;
    if (!adaSlot) stage = 'unassigned';
    else if (!sent) stage = 'ready';
    else if (days != null && days < 0) stage = 'expired';
    else if (days != null && days <= 7) stage = 'expiring';
    else stage = 'active';

    return {
        id, kind: 'sub', manual, email, name, phone: d.contactPhone || '', uid: d.uid || null,
        expiresAt, startedAt, days, stage, sent,
        createdAt: toDate(d.createdAt),
        accountText: d.accountEmail || '',
        hadKredit: Number.isSafeInteger(d.hadKredit) ? d.hadKredit : null,
        /* Bayaran MANUAL asal — Orders + Dashboard mengira ini, bukan lastPaidAt
           (yang berubah bila customer manual kemudian renew melalui Billplz). */
        manualPaidAt: toDate(d.manualPaidAt),
        manualSen: Number(d.manualPriceSen) || null,
        accountId: d.accountId || null,
        slotIdx: Number.isInteger(d.slotId) ? d.slotId : null,
        priceSen: Number(d.priceSenAtSignup) || 10000,
        variant: d.variant || 'sharing',
        renewCount: Number(d.renewCount) || 0,
        reminded: !!toDate(d.lastRemindedAt),
        remindedAt: toDate(d.lastRemindedAt),
        status: d.status || 'active',
        paidAgo: startedAt ? Math.max(0, Math.round((kini - startedAt) / DAY_MS)) : 0,
        /* PLAN_FLOW_PRODUK.md Fasa 0–1: bayaran SEBENAR + produk + notes. */
        jenis: d.jenis === 2 ? 2 : 1,
        productId: d.productId || null,
        productName: d.productName || '',
        note: d.note || '',
        paidAt: toDate(d.lastPaidAt) || startedAt,
        paidSen: Number(d.lastAmountSen) || Number(d.priceSenAtSignup) || null,
        channel: d.paymentChannel || (manual ? 'manual' : 'billplz'),
    };
}

function petakanAkaun(id, d) {
    const variant = d.variant || 'sharing';
    return {
        id,
        email: d.email || id,
        parentEmail: d.parentEmail || '',
        variant,
        priv: variant === 'private',
        cap: Number.isInteger(d.cap) ? d.cap : (variant === 'private' ? 1 : 4),
        costSen: Number(d.costSen) || 0,
        chargeDate: toDate(d.chargeDate),
        card: d.card || '',
        device: d.device || '',
        credits: Number(d.credits) || 0,
    };
}

/**
 * Penyewa yang dimasukkan tangan. Bentuk pulangannya SAMA seperti petakanSub,
 * supaya skrin tidak perlu tahu seseorang itu datang daripada bayaran gateway
 * atau daripada borang admin — kecuali satu medan, `manual`, yang dipakai untuk
 * menunjukkan lencana dan membenarkan suntingan.
 */
function petakanTenant(id, d, kini) {
    const expiresAt = toDate(d.expiresAt);
    const days = bakiHari(expiresAt, kini);
    const sent = !!d.accessSent;

    let stage;
    if (!sent) stage = 'ready';
    else if (days != null && days < 0) stage = 'expired';
    else if (days != null && days <= 7) stage = 'expiring';
    else stage = 'active';

    return {
        id, kind: 'tenant', manual: true,
        manualPaidAt: toDate(d.paidAt),
        manualSen: toDate(d.paidAt) ? (Number(d.priceSen) || null) : null,
        email: d.email || '',
        name: (d.name || '').trim(),
        phone: d.phone || '',
        uid: null,
        expiresAt, startedAt: toDate(d.startedAt) || toDate(d.createdAt), days, stage, sent,
        paidAt: toDate(d.paidAt),
        /* Tarikh ia DIMASUKKAN (bukan tarikh mula langganan) — dipakai oleh
           susunan "Latest/Oldest" supaya pelanggan yang baru ditambah naik ke
           atas walaupun tarikh mulanya diundurkan. */
        createdAt: toDate(d.createdAt),
        jenis: d.jenis === 2 ? 2 : 1,
        hadKredit: Number.isSafeInteger(d.hadKredit) ? d.hadKredit : null,
        /* Akaun disimpan sebagai TEKS, bukan rujukan kepada flow_accounts —
           koleksi itu belum wujud, dan pemilik perlu boleh menaip apa sahaja
           yang tertulis dalam Sheet dia tanpa menunggu import. */
        accountId: null,
        accountText: d.accountEmail || '',
        slotIdx: null,
        priceSen: Number(d.priceSen) || 10000,
        variant: 'sharing',
        renewCount: 0,
        reminded: !!toDate(d.lastRemindedAt),
        remindedAt: toDate(d.lastRemindedAt),
        note: d.note || '',
        status: 'active',
        paidAgo: toDate(d.paidAt) ? Math.max(0, Math.round((kini - toDate(d.paidAt)) / DAY_MS)) : 0,
        productId: d.productId || null,
        productName: d.productName || '',
        paidSen: Number(d.priceSen) || null,
        channel: 'manual',
    };
}

/** Tambah penyewa baharu yang dimasukkan tangan. */
export async function tambahPenyewa({ phone, name, email, accountEmail, expiresAt, priceRM, accessSent, note, jenis = 1, startedAt, paidAt, hadKredit = null }) {
    const nombor = String(phone || '').replace(/\D/g, '');
    if (nombor.length < 8) throw new Error('Invalid WhatsApp number.');
    if (!(expiresAt instanceof Date) || Number.isNaN(expiresAt.getTime())) {
        throw new Error('Invalid expiry date.');
    }
    const sah = x => x instanceof Date && !Number.isNaN(x.getTime());
    if (!sah(startedAt)) throw new Error('Invalid start date.');
    if (!sah(paidAt)) throw new Error('Invalid payment date.');
    if (expiresAt <= startedAt) throw new Error('Expiry must be after the start date.');
    if (jenis === 2 && (!Number.isSafeInteger(hadKredit) || hadKredit <= 0)) throw new Error('Enter a credit limit for type 2.');
    const ref = await addDoc(collection(db, TENANTS), {
        phone: nombor,
        name: (name || '').trim(),
        email: (email || '').trim().toLowerCase(),
        accountEmail: (accountEmail || '').trim(),
        expiresAt,
        priceSen: Math.round((Number(priceRM) || 100) * 100),
        accessSent: !!accessSent,
        note: (note || '').trim(),
        jenis: jenis === 2 ? 2 : 1,
        startedAt,
        paidAt,
        hadKredit: jenis === 2 ? hadKredit : null,
        source: 'manual',
        createdAt: serverTimestamp(),
        createdBy: auth.currentUser?.email || '',
    });
    return ref.id;
}

/** Jualan Flow Ultra manual (transfer) untuk Dashboard admin — hanya rekod yang ada tarikh bayaran.
 *  Rekod lama tanpa paidAt SENGAJA tidak dikira: ia jualan Sheet lama, bukan jualan bulan ia diimport. */
export async function jualanManualFlow() {
    /* Fasa 3A: jualan manual = flow_subs (source manual, medan manualPaidAt/manualPriceSen)
       + penyewa lama flow_tenants yang BELUM dipindah (flow_subs/m_<id> belum wujud).
       Yang sudah dipindah dilangkau — kalau tidak, satu jualan dikira dua kali. */
    const [subSnap, tenSnap] = await Promise.all([
        getDocs(query(collection(db, SUBS), where('source', '==', 'manual'))),
        getDocs(collection(db, TENANTS)).catch(() => ({ docs: [] })),
    ]);
    const idSub = new Set(subSnap.docs.map((d) => d.id));
    return [
        ...subSnap.docs.map((d) => d.data())
            .map((x) => ({ ms: toDate(x.manualPaidAt)?.getTime() ?? null, sen: Number(x.manualPriceSen) || 0 })),
        ...tenSnap.docs.filter((d) => !idSub.has('m_' + d.id) && !d.data().migratedTo).map((d) => d.data())
            .map((x) => ({ ms: toDate(x.paidAt)?.getTime() ?? null, sen: Number(x.priceSen) || 0 })),
    ].filter((x) => x.ms != null && x.sen > 0);
}

/** Kemas kini penyewa manual (cth tanda akses sudah dihantar / peringatan). */
export async function kemasPenyewa(id, patch) {
    /* `source` disertakan pada setiap tulisan kerana peraturan keselamatan
       menuntutnya hadir — tanpa ia, kemas kini ditolak dengan senyap. */
    await updateDoc(doc(db, TENANTS, id), { ...patch, source: 'manual' });
}

export async function padamPenyewa(id) {
    await deleteDoc(doc(db, TENANTS, id));
}

/** Tandakan peringatan sudah dihantar — untuk penyewa manual sahaja. */
export async function tandaDiingatkan(id) {
    await kemasPenyewa(id, { lastRemindedAt: serverTimestamp() });
}

/**
 * ⚠️ Baca akaun secara berhati-hati.
 * `flow_accounts` belum wujud dan belum ada peraturan keselamatannya, jadi
 * bacaan ini DITOLAK sehingga Sheet diimport. Itu keadaan yang dijangka, bukan
 * kerosakan — ia dianggap kosong dan skrin memapar keadaan kosong yang
 * menerangkannya. Bila koleksi itu diisi nanti, skrin hidup dengan sendirinya.
 */
async function bacaAkaun() {
    try {
        const snap = await getDocs(collection(db, ACCOUNTS));
        return { senarai: snap.docs.map((x) => petakanAkaun(x.id, x.data())), sedia: true };
    } catch {
        return { senarai: [], sedia: false };
    }
}

/**
 * Susun akaun + langganan jadi pokok yang skrin guna:
 *   induk (bil Google) → akaun family sharing → slot
 *
 * Slot dibina daripada `cap` akaun, kemudian diisi mengikut `slotIdx` penyewa.
 * Penyewa yang slotnya bertindih atau melebihi had disambung di HUJUNG dan
 * BUKAN dibuang — akaun terlebih isi mesti kelihatan, bukan hilang senyap.
 */
function binaPokok(akaun, subs, kini) {
    const ikutAkaun = new Map();
    subs.forEach((s) => {
        if (!s.accountId) return;
        if (!ikutAkaun.has(s.accountId)) ikutAkaun.set(s.accountId, []);
        ikutAkaun.get(s.accountId).push(s);
    });

    const lengkap = akaun.map((a) => {
        const t = Array.from({ length: a.cap }, () => null);
        const milik = (ikutAkaun.get(a.id) || []).slice()
            .sort((u, v) => (u.slotIdx ?? 99) - (v.slotIdx ?? 99));
        milik.forEach((s) => {
            const boleh = s.slotIdx != null && s.slotIdx < a.cap && t[s.slotIdx] === null;
            const i = boleh ? s.slotIdx : t.indexOf(null);
            /* `p` ialah nama medan yang TenantRow lama guna untuk nombor telefon.
               Dikekalkan supaya kad slot tidak perlu ditulis semula. */
            const item = { ...s, p: s.phone, d: s.days };
            if (i >= 0) t[i] = item; else t.push(item);
        });
        return { ...a, t };
    });

    const indukMap = new Map();
    lengkap.forEach((a) => {
        const kunci = a.parentEmail || a.email;
        if (!indukMap.has(kunci)) {
            indukMap.set(kunci, {
                email: kunci,
                cost: (a.costSen || 0) / 100,
                chargeDate: a.chargeDate,
                charge: a.chargeDate
                    ? a.chargeDate.toLocaleDateString('en-MY', { day: 'numeric', month: 'short' })
                    : '—',
                chargeIn: a.chargeDate ? bakiHari(a.chargeDate, kini) : 999,
                card: a.card, device: a.device, credits: a.credits,
                promo: '', accounts: [],
            });
        }
        indukMap.get(kunci).accounts.push(a);
    });

    return [...indukMap.values()].map((p, i) => ({
        ...p,
        no: i + 1,
        late: p.chargeIn != null && p.chargeIn < 0,
    }));
}

/**
 * Satu bacaan untuk seluruh tab.
 *   subs       — semua langganan, sudah dipetakan, susun ikut yang tamat dahulu
 *   parents    — pokok induk → akaun → slot (kosong sehingga Sheet diimport)
 *   orders     — rekod duit Flow Ultra
 *   akaunSedia — false bermakna `flow_accounts` belum boleh dibaca lagi
 */
export async function muatDataFlow(kini = new Date()) {
    const [subsSnap, akaun, ordSnap, tenantSnap] = await Promise.all([
        getDocs(collection(db, SUBS)),
        bacaAkaun(),
        getDocs(query(collection(db, ORDERS), where('type', '==', 'flowultra'))),
        /* Sama seperti akaun: koleksi ini mungkin belum ada peraturannya
           dideploy, jadi kegagalan dianggap "belum ada", bukan ralat. */
        getDocs(collection(db, TENANTS)).catch(() => ({ docs: [] })),
    ]);

    const bayar = subsSnap.docs
        .map((d) => petakanSub(d.id, d.data(), kini))
        /* Langganan dibatalkan tidak menduduki slot dan tidak perlu dikejar. Ia
           kekal dalam rekod duit (orders), cuma tidak dalam senarai kerja. */
        .filter((s) => s.status !== 'cancelled');

    /* Fasa 3A: penyewa lama yang SUDAH dipindah ke flow_subs (id m_<idLama>) dilangkau —
       satu orang, satu baris. Yang belum dipindah masih dipapar seperti dahulu. */
    const idSub = new Set(subsSnap.docs.map((d) => d.id));
    /* `migratedTo` = digabung ke langganan lain (cth baris manual pendua bagi pembeli Billplz). */
    const manual = tenantSnap.docs.filter((d) => !idSub.has('m_' + d.id) && !d.data().migratedTo)
        .map((d) => petakanTenant(d.id, d.data(), kini));

    /* Digabung SELEPAS dipetakan, bukan disimpan bersama. Skrin melihat satu
       senarai; pangkalan data kekal dua tempat (lihat nota firestore.rules). */
    const subs = [...bayar, ...manual]
        .sort((a, b) => (a.days ?? 9999) - (b.days ?? 9999));

    const orders = ordSnap.docs.map((d) => {
        const x = d.data();
        return {
            id: d.id,
            email: x.email || '',
            uid: x.uid || null,
            amountSen: Number(x.paidAmountSen ?? x.amountSen) || 0,
            status: x.status || 'pending',
            paidAt: toDate(x.paidAt),
            createdAt: toDate(x.createdAt),
            isRenewal: !!x.isRenewal,
            receipt: x.billplzUrl || '',
            productId: x.productId || null,
            productName: x.productName || '',
            days: Number.isSafeInteger(x.durationDays) ? x.durationDays : null,
            channel: x.paymentChannel || '',
            /* Resit manual (Extend days → Paid renewal, 2026-10-14): provider 'manual', tiada pautan resit. */
            provider: x.provider === 'manual' ? 'manual' : 'billplz',
            flowSubId: x.flowSubId || null,
            name: x.name && x.name !== x.email ? x.name : '',
            phone: x.whatsappNumber || '',
        };
    }).sort((a, b) => (b.createdAt?.getTime() || 0) - (a.createdAt?.getTime() || 0));

    return {
        subs,
        orders,
        parents: binaPokok(akaun.senarai, subs, kini),
        akaunSedia: akaun.sedia,
        bilAkaun: akaun.senarai.length,
        bilManual: manual.length,
        bilBayar: bayar.length,
    };
}
